import { useEffect, useRef, useState } from 'react';
import { OpenLocationCode } from 'open-location-code';
import { searchAddress, type Geo } from '../api/geocoding';

type Props = {
  /** valore corrente del campo indirizzo */
  value: string;
  /** chiamato a ogni keystroke */
  onChange: (v: string) => void;
  /** chiamato quando l'utente seleziona un risultato dal dropdown */
  onPick: (result: Geo) => void;
  placeholder?: string;
};

const DEBOUNCE_MS = 350;
const MIN_CHARS = 3;

// Plus Code regex: 4-8 caratteri OLC, "+", 2-3 caratteri OLC, opzionalmente
// seguito da spazio + nome località per i codici corti.
// Set caratteri OLC: 23456789CFGHJMPQRVWX (no 0,1,A,B,D,E,I,K,L,N,O,S,T,U,Y,Z)
const PLUS_CODE_RE =
  /^([23456789CFGHJMPQRVWX]{4,8}\+[23456789CFGHJMPQRVWX]{2,3})(?:\s+(.+))?$/i;

type ResultItem = Geo & { isHint?: boolean };

const olc = new OpenLocationCode();

function shortLocality(displayName: string): string {
  return displayName.split(',').slice(0, 2).join(',').trim();
}

function normalize(s: string): string {
  return s.trim();
}

export function AddressSearch({ value, onChange, onPick, placeholder }: Props) {
  const [results, setResults] = useState<ResultItem[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState<number>(-1);
  const skipNextFetch = useRef(false);
  // true SOLO quando l'ultimo cambio di `value` è arrivato dalla digitazione
  // dell'utente. Quando il parent setta `value` programmaticamente (es. un
  // pre-fill in startEdit), questo ref resta false e la ricerca non parte:
  // così aprire la modalità "Modifica" non fa comparire automaticamente la
  // tendina dei risultati.
  const userTypedRef = useRef(false);

  useEffect(() => {
    if (skipNextFetch.current) {
      skipNextFetch.current = false;
      return;
    }
    if (!userTypedRef.current) {
      // cambio "esterno" del value: niente fetch e niente apertura tendina
      return;
    }
    userTypedRef.current = false;
    const trimmed = normalize(value);
    if (trimmed.length < MIN_CHARS) {
      setResults(null);
      setOpen(false);
      return;
    }

    let cancelled = false;
    const handle = setTimeout(async () => {
      // Plus Code detection — niente debounce per i codici full (decode locale)
      const m = trimmed.match(PLUS_CODE_RE);
      if (m) {
        const code = m[1]!.toUpperCase();
        const locality = m[2]?.trim();

        if (olc.isFull(code)) {
          const area = olc.decode(code);
          if (cancelled) return;
          setResults([
            {
              displayName: `Plus Code: ${code}`,
              lat: area.latitudeCenter,
              lon: area.longitudeCenter,
              category: 'code',
              type: 'plus_code',
            },
          ]);
          setOpen(true);
          setHighlight(-1);
          return;
        }

        if (olc.isShort(code)) {
          if (!locality) {
            if (cancelled) return;
            setResults([
              {
                displayName: `Plus Code corto — aggiungi il nome della città dopo lo spazio (es. "${code} Cortina")`,
                lat: 0,
                lon: 0,
                category: 'hint',
                type: 'plus_code_short',
                isHint: true,
              },
            ]);
            setOpen(true);
            setHighlight(-1);
            return;
          }

          // Geocode località per recovery del Plus Code corto
          setLoading(true);
          try {
            const refs = await searchAddress(locality, 1);
            if (cancelled) return;
            if (refs[0]) {
              const fullCode = olc.recoverNearest(code, refs[0].lat, refs[0].lon);
              const area = olc.decode(fullCode);
              setResults([
                {
                  displayName: `Plus Code: ${code} — ${shortLocality(refs[0].displayName)}`,
                  lat: area.latitudeCenter,
                  lon: area.longitudeCenter,
                  category: 'code',
                  type: 'plus_code',
                },
              ]);
            } else {
              setResults([
                {
                  displayName: `Località "${locality}" non trovata — verifica il nome`,
                  lat: 0,
                  lon: 0,
                  category: 'hint',
                  type: 'plus_code_short',
                  isHint: true,
                },
              ]);
            }
            setOpen(true);
            setHighlight(-1);
          } catch {
            if (!cancelled) {
              setResults([]);
              setOpen(true);
            }
          } finally {
            if (!cancelled) setLoading(false);
          }
          return;
        }
      }

      // Fallback: ricerca Nominatim normale
      setLoading(true);
      try {
        const r = await searchAddress(trimmed);
        if (cancelled) return;
        setResults(r);
        setOpen(true);
        setHighlight(-1);
      } catch {
        if (!cancelled) {
          setResults([]);
          setOpen(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [value]);

  function selectResult(r: ResultItem) {
    if (r.isHint) return; // gli hint non sono selezionabili
    skipNextFetch.current = true;
    onChange(r.displayName);
    setResults(null);
    setOpen(false);
    setHighlight(-1);
    onPick(r);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!open || !results || results.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => (h + 1) % results.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => (h <= 0 ? results.length - 1 : h - 1));
    } else if (e.key === 'Enter' && highlight >= 0 && results[highlight]) {
      e.preventDefault();
      selectResult(results[highlight]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }

  return (
    <div className="address-search">
      <input
        type="text"
        value={value}
        onChange={(e) => {
          userTypedRef.current = true;
          onChange(e.target.value);
        }}
        onFocus={() => results && results.length > 0 && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={onKeyDown}
        placeholder={placeholder ?? 'Cerca luogo, indirizzo, campeggio o Plus Code…'}
        autoComplete="off"
        spellCheck={false}
      />
      {loading && <div className="address-loading">…</div>}
      {open && results && results.length > 0 && (
        <ul className="address-dropdown" role="listbox">
          {results.map((r, i) => (
            <li
              key={`${r.lat}-${r.lon}-${i}`}
              role="option"
              aria-selected={i === highlight}
              className={`${i === highlight ? 'highlighted' : ''} ${r.isHint ? 'hint' : ''}`}
              onMouseDown={(e) => {
                e.preventDefault();
                selectResult(r);
              }}
              onMouseEnter={() => !r.isHint && setHighlight(i)}
            >
              <div className="result-name">
                {r.type === 'plus_code' && <span className="plus-badge">📍 Plus</span>}
                {r.displayName}
              </div>
              {!r.isHint && (r.type || r.category) && r.type !== 'plus_code' && (
                <div className="result-meta">
                  {r.type ?? r.category}
                  <span className="result-coords">
                    {' · '}
                    {r.lat.toFixed(4)}, {r.lon.toFixed(4)}
                  </span>
                </div>
              )}
              {r.type === 'plus_code' && (
                <div className="result-meta">
                  <span className="result-coords">
                    {r.lat.toFixed(5)}, {r.lon.toFixed(5)}
                  </span>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {open && results && results.length === 0 && !loading && (
        <ul className="address-dropdown empty">
          <li>Nessun risultato</li>
        </ul>
      )}
    </div>
  );
}
